import { db, auth, secondaryAuth } from './firebase';
import {
  collection, doc, getDoc, getDocs, addDoc, setDoc, updateDoc, deleteDoc,
  query, where, orderBy, serverTimestamp
} from 'firebase/firestore';

// ==================== AUTH ====================

export async function loginUser(cedula, password) {
  const email = `${cedula}@visitas.app`;
  const { signInWithEmailAndPassword } = await import('firebase/auth');
  const cred = await signInWithEmailAndPassword(auth, email, password);
  const userDoc = await getDoc(doc(db, 'users', cred.user.uid));
  if (!userDoc.exists()) throw new Error('Usuario no encontrado');
  return { uid: cred.user.uid, ...userDoc.data() };
}

export async function changePassword(passwordActual, passwordNueva) {
  const { signInWithEmailAndPassword, updatePassword } = await import('firebase/auth');
  const user = auth.currentUser;
  if (!user) throw new Error('No autenticado');
  await signInWithEmailAndPassword(auth, user.email, passwordActual);
  await updatePassword(user, passwordNueva);
}

export async function getCurrentUser() {
  const user = auth.currentUser;
  if (!user) throw new Error('No autenticado');
  const userDoc = await getDoc(doc(db, 'users', user.uid));
  if (!userDoc.exists()) throw new Error('Usuario no encontrado');
  return { uid: user.uid, ...userDoc.data() };
}

// ==================== RESIDENTES (ADMIN) ====================

export async function getResidentes() {
  const q = query(collection(db, 'users'), where('rol', '==', 'residente'));
  const snap = await getDocs(q);
  // Ordenar en cliente para evitar necesidad de índice compuesto
  const results = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  results.sort((a, b) => (b.createdAt?.toDate?.() || 0) - (a.createdAt?.toDate?.() || 0));
  return results;
}

export async function createResidente(data) {
  const { createUserWithEmailAndPassword } = await import('firebase/auth');
  const email = `${data.cedula}@visitas.app`;
  const password = '123456789';

  const existQ = query(collection(db, 'users'), where('cedula', '==', data.cedula));
  const existSnap = await getDocs(existQ);
  if (!existSnap.empty) throw new Error('Ya existe un usuario con esa cédula');

  // Usar secondaryAuth para NO reemplazar la sesión del admin
  const cred = await createUserWithEmailAndPassword(secondaryAuth, email, password);

  // Cerrar sesión de la instancia secundaria inmediatamente
  const { signOut } = await import('firebase/auth');
  await signOut(secondaryAuth);

  await setDoc(doc(db, 'users', cred.user.uid), {
    uid: cred.user.uid,
    cedula: data.cedula,
    nombre_completo: data.nombre_completo,
    celular: data.celular,
    apartamento: data.apartamento,
    piso: data.piso,
    rol: 'residente',
    email,
    createdAt: serverTimestamp()
  });

  return { cedula: data.cedula, nombre_completo: data.nombre_completo, password_default: password };
}

export async function updateResidente(id, data) {
  await updateDoc(doc(db, 'users', id), {
    nombre_completo: data.nombre_completo,
    celular: data.celular,
    apartamento: data.apartamento,
    piso: data.piso
  });
}

export async function deleteResidente(id) {
  await deleteDoc(doc(db, 'users', id));
}

// ==================== QR ====================

export async function generarQR(data) {
  const user = auth.currentUser;
  if (!user) throw new Error('No autenticado');
  const token = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substr(2) + Date.now().toString(36);

  const residenteDoc = await getDoc(doc(db, 'users', user.uid));
  const residente = residenteDoc.data();

  // Soporte para múltiples visitantes
  const visitantes = data.visitantes || [{ nombre: data.visitante_nombre, cedula: data.visitante_cedula }];
  // Soporte para múltiples placas
  const placas = data.placas || (data.placa_vehiculo ? [data.placa_vehiculo] : []);

  const qrRef = await addDoc(collection(db, 'codigos_qr'), {
    residenteId: user.uid,
    visitantes: visitantes,
    placas: placas,
    motivo: data.motivo,
    fecha_caducidad: data.fecha_caducidad,
    token,
    usado: false,
    createdAt: serverTimestamp()
  });

  return {
    id: qrRef.id,
    token,
    visitantes,
    placas,
    motivo: data.motivo,
    fecha_caducidad: data.fecha_caducidad,
    residente_nombre: residente.nombre_completo,
    apartamento: residente.apartamento,
    piso: residente.piso
  };
}

export async function getMisQR() {
  const user = auth.currentUser;
  if (!user) throw new Error('No autenticado');
  const q = query(collection(db, 'codigos_qr'), where('residenteId', '==', user.uid), orderBy('createdAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function escanearQR(qrData) {
  const user = auth.currentUser;
  if (!user) throw new Error('No autenticado');

  let parsed;
  try {
    parsed = typeof qrData === 'string' ? JSON.parse(qrData) : qrData;
  } catch (e) {
    throw new Error('QR inválido: no se pudo leer la información');
  }

  if (!parsed.token) throw new Error('QR inválido: falta el token');
  const { token } = parsed;

  const q = query(collection(db, 'codigos_qr'), where('token', '==', token));
  const snap = await getDocs(q);
  if (snap.empty) throw new Error('QR no encontrado en el sistema');

  const qrDoc = snap.docs[0];
  const qrRecord = { id: qrDoc.id, ...qrDoc.data() };

  if (qrRecord.usado) throw new Error('Este QR ya fue utilizado');

  const hoy = new Date().toISOString().split('T')[0];
  if (qrRecord.fecha_caducidad < hoy) throw new Error('Este QR ha caducado');

  // Marcar como usado
  await updateDoc(doc(db, 'codigos_qr', qrDoc.id), { usado: true });

  // Registrar visita con múltiples visitantes
  const visitantes = parsed.visitantes || qrRecord.visitantes || [{ nombre: parsed.visitante_nombre || qrRecord.visitante_nombre, cedula: parsed.visitante_cedula || qrRecord.visitante_cedula }];
  const placas = parsed.placas || qrRecord.placas || (parsed.placa_vehiculo ? [parsed.placa_vehiculo] : []) || (qrRecord.placa_vehiculo ? [qrRecord.placa_vehiculo] : []);

  await addDoc(collection(db, 'visitas'), {
    qrId: qrDoc.id,
    vigilanteId: user.uid,
    residenteId: qrRecord.residenteId,
    visitantes: visitantes,
    placas: placas,
    motivo: parsed.motivo || qrRecord.motivo,
    foto_placa_url: parsed.foto_placa_url || null,
    foto_cedula_url: parsed.foto_cedula_url || null,
    escaneadoEn: serverTimestamp()
  });

  return { success: true, message: 'Visita registrada exitosamente' };
}

// ==================== VISITAS ====================

export async function getVisitas() {
  const q = query(collection(db, 'visitas'), orderBy('escaneadoEn', 'desc'));
  const snap = await getDocs(q);
  const visitas = [];
  for (const d of snap.docs) {
    const v = { id: d.id, ...d.data() };
    const resDoc = await getDoc(doc(db, 'users', v.residenteId));
    const vigDoc = await getDoc(doc(db, 'users', v.vigilanteId));
    if (resDoc.exists()) {
      v.residente_nombre = resDoc.data().nombre_completo;
      v.apartamento = resDoc.data().apartamento;
      v.piso = resDoc.data().piso;
    }
    if (vigDoc.exists()) v.vigilante_nombre = vigDoc.data().nombre_completo;
    visitas.push(v);
  }
  return visitas;
}

export async function getMisVisitas() {
  const user = auth.currentUser;
  if (!user) throw new Error('No autenticado');
  const q = query(collection(db, 'visitas'), where('residenteId', '==', user.uid), orderBy('escaneadoEn', 'desc'));
  const snap = await getDocs(q);
  const visitas = [];
  for (const d of snap.docs) {
    const v = { id: d.id, ...d.data() };
    if (v.vigilanteId) {
      const vigDoc = await getDoc(doc(db, 'users', v.vigilanteId));
      if (vigDoc.exists()) v.vigilante_nombre = vigDoc.data().nombre_completo;
    }
    visitas.push(v);
  }
  return visitas;
}

export async function buscarVisitas(cedula) {
  const q = query(collection(db, 'visitas'), where('visitante_cedula', '==', cedula), orderBy('escaneadoEn', 'desc'));
  const snap = await getDocs(q);
  const visitas = [];
  for (const d of snap.docs) {
    const v = { id: d.id, ...d.data() };
    const resDoc = await getDoc(doc(db, 'users', v.residenteId));
    if (resDoc.exists()) {
      v.residente_nombre = resDoc.data().nombre_completo;
      v.apartamento = resDoc.data().apartamento;
      v.piso = resDoc.data().piso;
    }
    visitas.push(v);
  }
  return visitas;
}

// ==================== REPORTES ====================

export async function createReport(data) {
  const user = auth.currentUser;
  if (!user) throw new Error('No autenticado');
  const reportRef = await addDoc(collection(db, 'reportes'), {
    visitaId: data.visitaId || null,
    vigilanteId: data.vigilanteId || user.uid,
    vigilante_nombre: data.vigilante_nombre || '',
    residenteId: data.residenteId || null,
    texto: data.texto || '',
    fotos: data.fotos || [],
    categoria: data.categoria || 'anomalia',
    tipo: data.tipo || 'vigilancia', // 'vigilancia' o 'general'
    hashtags: data.hashtags || [],
    autor_nombre: data.autor_nombre || '',
    createdAt: serverTimestamp()
  });
  return { id: reportRef.id };
}

export async function getReports() {
  const q = query(collection(db, 'reportes'), orderBy('createdAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function updateReportVeredicto(reporteId, veredicto) {
  await updateDoc(doc(db, 'reportes', reporteId), {
    veredicto: veredicto,
    veredictoAt: serverTimestamp()
  });
}

// Obtener todos los residentes para autocompletado de hashtags
export async function getResidentesForTags() {
  const q = query(collection(db, 'users'), where('rol', '==', 'residente'));
  const snap = await getDocs(q);
  // Ordenar manualmente en el cliente para evitar necesidad de índice compuesto
  const results = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  results.sort((a, b) => (b.createdAt?.toDate?.() || 0) - (a.createdAt?.toDate?.() || 0));
  return results;
}

// ==================== APELACIONES ====================

export async function createApelacion(data) {
  const user = auth.currentUser;
  if (!user) throw new Error('No autenticado');
  const ref = await addDoc(collection(db, 'apelaciones'), {
    reporteId: data.reporteId,
    residenteId: user.uid,
    residente_nombre: data.residente_nombre || '',
    texto: data.texto || '',
    fotos: data.fotos || [],
    hashtags: data.hashtags || [],
    createdAt: serverTimestamp()
  });
  return { id: ref.id };
}

export async function getApelaciones() {
  const q = query(collection(db, 'apelaciones'), orderBy('createdAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}